import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { usePostEngagement } from "../../hooks/usePostEngagement";
import PostHeader from "./PostHeader";
import PostStatsRow from "./PostStatsRow";
import PostExerciseList from "./PostExerciseList";
import PostAchievements from "./PostAchievements";
import PostImage from "./PostImage";
import PostEngagementBar from "./PostEngagementBar";
import PostCommentsSheet from "./PostCommentsSheet";
import "./WorkoutPostCard.css";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const WorkoutPostCard = ({ post }) => {
  const { user: authUser } = useAuth();
  const { toggleLike, toggleSave } = usePostEngagement();
  const [commentsOpen, setCommentsOpen] = useState(false);

  // On the current user's own posts, show their real account name/avatar
  // rather than the mock author (posts are otherwise author-agnostic mock
  // data — see data/mock/workoutPosts.js).
  const displayUser = post.isMine
    ? {
        name: authUser?.name || post.user.name,
        avatarColor: "var(--neon)",
      }
    : post.user;

  const handleShare = async () => {
    const shareData = {
      title: post.title,
      text: `${displayUser.name}'s ${post.title} workout on DevFit`,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // user cancelled — no-op
      }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareData.text);
    }
  };

  return (
    <motion.article
      className="wpc"
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
    >
      <PostHeader user={displayUser} createdAt={post.createdAt} />

      <div className="wpc-title-block">
        <h3 className="wpc-workout-title">💪 {post.title}</h3>
        <p className="wpc-muscle-groups">{post.muscleGroups.join(" + ")}</p>
      </div>

      <PostStatsRow stats={post.stats} />
      <PostAchievements achievements={post.achievements} />

      <PostImage
        postId={post.id}
        imageUrl={post.imageUrl}
        workoutTitle={post.title}
        muscleGroups={post.muscleGroups}
      />

      <PostExerciseList exercises={post.exercises} />

      <PostEngagementBar
        likeCount={post.likeCount}
        commentCount={post.commentCount}
        shareCount={post.shareCount}
        isLiked={post.isLiked}
        isSaved={post.isSaved}
        onLike={() => toggleLike(post.id, post.isLiked, post.likeCount)}
        onComment={() => setCommentsOpen(true)}
        onShare={handleShare}
        onSave={() => toggleSave(post.id, post.isSaved)}
      />

      <PostCommentsSheet
        postId={post.id}
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
      />
    </motion.article>
  );
};

export default WorkoutPostCard;
